import * as React from "react";
import { useSupabase } from "../../utils/supabase/useSupabase";
import { SupabaseClient, User } from "@supabase/supabase-js";
import { executeSupabaseQuery, useQuery } from "../../utils/supabase/useQuery";
import * as t from "io-ts";
import {
    athleteMessages,
    athleteTable,
    FullName,
    fullNameCodec,
    sexCodec,
    sexMessages,
    switchForenameAndSurname,
    wellKnownUserMetadataCodec,
} from "../../domain/user";
import {
    EventId,
    raceCategoryTable,
    RaceId,
    raceIdCodec,
    raceMessages,
    raceTable,
    RegistrationField,
} from "../../domain/event";
import {
    categoriesTable,
    categoryLabel,
    categoryMessages,
    isCategoryForAthlete,
} from "../../domain/category";
import { assertUnreachable } from "../../utils/assertUnreachable";
import {
    array,
    either,
    map,
    nonEmptyArray,
    number,
    option,
    ord,
    record,
    task,
    taskEither,
} from "fp-ts";
import { Ord } from "fp-ts/Ord";
import { constNull, flow, identity, pipe } from "fp-ts/function";
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    Grid,
    Link,
    MenuItem,
    Step,
    StepLabel,
    Stepper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { sequenceS } from "fp-ts/Apply";
import { queryState, queryStateData } from "../../utils/supabase/QueryState";
import {
    emptyArray,
    mapFromArray,
    omit,
    optionOrChain,
    pick,
    rightOrThrow,
    runTask,
    someOrThrow,
    updateArrayWhere,
} from "../../utils/fp";
import { Option } from "fp-ts/Option";
import {
    column,
    columns,
    insert,
    join,
    TableRowT,
} from "../../utils/pg-ts/pg-ts";
import { CenteredProgress } from "../common/CenteredProgress";
import { AthleteNameAutocomplete } from "./AthleteNameAutocomplete";
import { useAlertOnQueryError } from "../../hooks/useAlertOnQueryError";
import { CategoryLabel } from "../common/CategoryLabel";
import AddIcon from "@mui/icons-material/Add";
import { useGlobalAlertsSubject } from "../common/GlobalAlerts";
import { reportError } from "../../utils/reporting";
import {
    bornCodec,
    nonEmptyStringOrNullCodec,
    trimmedStringCodec,
    urlCodec,
    validIsoDateStringFromDateCodec,
    Year,
} from "../../domain/common";
import { FormValues, useForm } from "../form/form";
import { useTranslation } from "../../utils/i18n/lang";
import { messages, Messages } from "../../utils/i18n/i18n";
import { NumberFromString } from "io-ts-types";
import { Select } from "../form/Select";
import { Task } from "fp-ts/Task";
import { RequiredCheckbox } from "../form/RequiredCheckbox";
import { LoadingButton } from "@mui/lab";
import { CancelableDialogBody } from "../user/CancelableDialogBody";
import {
    DialogButton,
    DialogState,
    useDialogState,
} from "../common/DialogButton";
import { SexSelect } from "../user/SexSelect";
import { privacyRoute } from "../../routes/xathlo";
import { InternalLink } from "../common/Link";
import { ClubAutocomplete } from "../user/ClubAutocomplete";
import { OrganizationId } from "../../domain/organization";
import { useLocalStorage } from "../../utils/useLocaStorage";
import { registrationTable } from "../../domain/registration";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import {
    commonStringInputDef,
    RegistrationDynamicField,
} from "./RegistrationDynamicField";
import { raceNumberCodec } from "../../domain/result";
import { LocalCurrency } from "../common/LocalCurrency";
import { Cart } from "./Cart";
import { RegistrationStateView } from "./RegistrationState";
import { CartItem, PaymentMethod, paymentTable } from "../../domain/payment";

type RegistrationPayload = Pick<
    TableRowT<typeof registrationTable>,
    | "race_id"
    | "athlete_id"
    | "category_id"
    | "club"
    | "number"
    | "dynamic_answers"
>;

// answers to commonString fields, keyed by field id; null when none provided
const dynamicAnswersOf = (
    fields: Array<RegistrationField> | null,
    values: Record<string, unknown>
): Record<string, string> | null => {
    const entries = pipe(
        fields ?? [],
        array.filterMap((field) => {
            if (field.kind !== "commonString") {
                return option.none;
            }
            const value = values[field.id];
            return typeof value === "string" && value.length > 0
                ? option.some([field.id, value] as const)
                : option.none;
        })
    );
    return entries.length > 0 ? Object.fromEntries(entries) : null;
};

// registration row for `race` from the racer's answers in the Finish step
const registrationPayloadOf = (
    race: EventRace,
    athleteId: RegistrationPayload["athlete_id"],
    values: FinishFormValues
): RegistrationPayload => {
    // custom fields (raceNumber, commonString answers) are dynamic form keys
    // that FinishFormValues does not declare
    const answers = values as Record<string, unknown>;
    return {
        race_id: race.id,
        athlete_id: athleteId,
        category_id: values.categoryId,
        club: values.club,
        number: pipe(
            answers.raceNumber,
            raceNumberCodec.decode,
            option.fromEither,
            option.toNullable
        ),
        dynamic_answers: dynamicAnswersOf(race.registration_fields, answers),
    };
};

// form key for a custom registration field: commonString answers are keyed by
// their stable id; raceNumber keeps the legacy "raceNumber" key (-> `number`)
const customFieldKey = (field: RegistrationField): string =>
    field.kind === "commonString" ? field.id : field.kind;

const customFieldInputDef = (field: RegistrationField) => {
    switch (field.kind) {
        case "raceNumber":
            return { codec: raceNumberCodec };
        case "commonString":
            return commonStringInputDef(field);
    }
    assertUnreachable(field);
};

const executeRegistrationQuery =
    (supabase: SupabaseClient, racesQuery: ReturnType<typeof useEventRaces>) =>
    (payload: RegistrationPayload) =>
        pipe(
            racesQuery.state,
            queryStateData,
            option.chain(registrationFee(payload.race_id, payload.category_id)),
            taskEither.fromOption(() => ({
                message: "raceListNotYetLoaded",
            })),
            taskEither.chainW((fee) =>
                executeSupabaseQuery(
                    "raceRegister",
                    registrationInsertResultCodec,
                    flow(
                        insert(registrationTable, {
                            ...payload,
                            state: fee > 0 ? "unconfirmed" : "confirmed", // TODO security hole...
                        }),
                        (queryBuilder) => queryBuilder.single()
                    ),
                    supabase
                )
            ),
            taskEither.map(
                (newRegistration: RegistrationResult): Registration => ({
                    ...newRegistration,
                    [paymentAlias]: [],
                })
            )
        );

const registrationFee =
    (raceId: number, categoryId: number | null) => (races: EventRaces) =>
        pipe(
            races,
            array.findFirst((r) => r.id === raceId),
            option.map((r: EventRace) =>
                pipe(
                    r.race_category,
                    array.findFirst((c) => c.category_id === categoryId),
                    option.chain((c) => option.fromNullable(c.entry_fee)),
                    option.getOrElse(() => 0)
                )
            )
        );

interface Handlers {
    // resolves to whether the registration was stored
    onRegister: (p: RegistrationPayload) => Task<boolean>;
    onUnregister: (p: RegistrationResult) => void;
}

interface Props {
    user: User;
    eventId: EventId;
    eventYear: Year;
    organizationId: OrganizationId;
    eventRaceIdList: Array<RaceId>;
    paymentMethod: Nullable<PaymentMethod>;
    rules: Nullable<string>;
    // race preferred from selection (e.g. via ?raceId link) when an athlete
    // can register to multiple races
    preferredRaceId: Option<RaceId>;
}

const MyRegistrations = (p: Props) => {
    const myAthletes = useMyAthletesWithRaceRegistration(
        p.user,
        p.eventRaceIdList
    );
    const eventRaces = useEventRaces(p.eventId);

    const state = sequenceS(queryState)({
        athletes: myAthletes.state,
        races: eventRaces.state,
    });
    useAlertOnQueryError(state);

    const supabase = useSupabase();
    const alert$ = useGlobalAlertsSubject();

    // confirmation in the global snackbar once a registration is stored;
    // "unconfirmed" means an entry fee is still to be paid (see Cart)
    const notifyRegistered = (
        athleteName: string,
        registration: Pick<RegistrationResult, "race_id" | "state">
    ) => {
        const raceTitle = pipe(
            state,
            queryStateData,
            option.chain((data) =>
                pipe(
                    data.races,
                    array.findFirst((race) => race.id === registration.race_id)
                )
            ),
            option.map((race) => race.title)
        );
        const stored = pipe(
            raceTitle,
            option.fold(
                () => `${athleteName}: registrace proběhla úspěšně.`,
                (title) =>
                    `${athleteName}: registrace na ${title} proběhla úspěšně.`
            )
        );
        alert$.next({
            severity: "success",
            message:
                registration.state === "unconfirmed"
                    ? `${stored} Zbývá uhradit startovné.`
                    : stored,
        });
    };

    const onRegister = (payload: RegistrationPayload) => {
        return pipe(
            payload,
            executeRegistrationQuery(supabase, eventRaces),
            task.map(
                either.fold(
                    (e) => {
                        reportError("registration failed", e);

                        alert$.next({
                            severity: "error",
                            message: pipe(e.message, (message) => {
                                switch (message) {
                                    // TODO constraint name in pg ts?
                                    case 'duplicate key value violates unique constraint "registrations_number_race_id_unique"':
                                        return "Požadované startovní číslo je již obsazeno";

                                    default:
                                        return "Registrace se nezdařila, administrátor byl kontaktován";
                                }
                            }),
                        });
                        return false;
                    },
                    (newRegistration) => {
                        myAthletes.mutate(
                            flow(
                                option.fromNullable,
                                option.chain(
                                    updateArrayWhere(
                                        (athlete) =>
                                            athlete.id === payload.athlete_id,
                                        (athlete) => ({
                                            ...athlete,
                                            [registrationAlias]: pipe(
                                                athlete[registrationAlias],
                                                array.append(newRegistration)
                                            ),
                                        })
                                    )
                                ),
                                option.toUndefined
                            )
                        );
                        notifyRegistered(
                            pipe(
                                state,
                                queryStateData,
                                option.chain((data) =>
                                    pipe(
                                        data.athletes,
                                        array.findFirst(
                                            (athlete) =>
                                                athlete.id ===
                                                payload.athlete_id
                                        )
                                    )
                                ),
                                option.map((athlete) => athlete.name),
                                option.getOrElse(() => "Závodník")
                            ),
                            newRegistration
                        );
                        return true;
                    }
                )
            )
        );
    };

    const onUnregister = (registration: RegistrationResult) => {
        pipe(
            executeSupabaseQuery(
                "raceUnRegister",
                t.unknown,
                (supabase) =>
                    supabase
                        .from(registrationTable.name)
                        .delete()
                        .eq(column(registrationTable, "id"), registration.id),
                supabase
            ),
            task.map(
                either.fold(
                    (e) => {
                        reportError("unregistration failed", e);
                        alert$.next({
                            severity: "error",
                            message:
                                "Storno registrace se nezdařilo, administrátor byl kontaktován",
                        });
                    },
                    () => {
                        myAthletes.mutate(
                            flow(
                                option.fromNullable,
                                option.chain(
                                    updateArrayWhere(
                                        (athlete) =>
                                            athlete.id ===
                                            registration.athlete_id,
                                        (athlete) => ({
                                            ...athlete,
                                            [registrationAlias]: pipe(
                                                athlete[registrationAlias],
                                                array.filter(
                                                    (r) =>
                                                        r.id !== registration.id
                                                )
                                            ),
                                        })
                                    )
                                ),
                                option.toUndefined
                            )
                        );
                    }
                )
            ),
            runTask
        );
    };

    const addAthleteDialogState = useDialogState();
    // registering an existing athlete picked in the add-athlete dialog
    const onRegisterFromDialog = (payload: RegistrationPayload) =>
        pipe(
            onRegister(payload),
            task.map((stored) => {
                if (stored) {
                    addAthleteDialogState.close();
                }
                return stored;
            })
        );
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const onAthleteCreate = (
        athlete: CreateAthleteFormValues,
        finishFormValues: FinishFormValues,
        race: EventRace
    ) => {
        return pipe(
            executeSupabaseQuery(
                "createAthlete",
                athleteInsertResultCodec,
                flow(
                    insert(athleteTable, {
                        ...athlete,
                        account_id: pipe(
                            // TODO do not use supabase user directly? Add some wrapping function
                            p.user.id,
                            athleteTable.columns.account_id.decode,
                            rightOrThrow
                        ),
                    }),
                    (queryBuilder) => queryBuilder.single()
                ),
                supabase
            ),
            taskEither.chain((createdAthlete) =>
                pipe(
                    {
                        ...registrationPayloadOf(
                            race,
                            createdAthlete.id,
                            finishFormValues
                        ),
                        club: finishFormValues.club || athlete.club,
                    },
                    executeRegistrationQuery(supabase, eventRaces),
                    taskEither.map((registration) => ({
                        createdAthlete,
                        registration,
                    }))
                )
            ),
            task.map((res) =>
                pipe(
                    res,
                    // side Effect
                    either.fold(
                        (e) => {
                            reportError("new athlete registration failed", e);
                            alert$.next({
                                severity: "error",
                                message:
                                    "Registrace závodníka se nezdařila, administrátor byl kontaktován",
                            });
                            return false;
                        },
                        (res) => {
                            myAthletes.mutate(
                                flow(
                                    option.fromNullable,
                                    option.map(
                                        array.append({
                                            ...res.createdAthlete,
                                            [registrationAlias]: [
                                                res.registration,
                                            ],
                                        })
                                    ),
                                    option.toUndefined
                                )
                            );
                            addAthleteDialogState.close();
                            notifyRegistered(athlete.name, res.registration);
                            return true;
                        }
                    )
                )
            )
        );
    };

    switch (state.type) {
        case "loading":
        case "error":
            return <CenteredProgress />;

        case "completed":
            const categories = categoriesFromRacesTmp(state.data.races);

            return pipe(
                state.data.athletes,
                array.sort(athleteOrdBySurname),
                nonEmptyArray.fromArray,
                option.fold(
                    () => (
                        <CreateAthlete
                            eventId={p.eventId}
                            existingAthletes={state.data.athletes}
                            onRegister={onRegisterFromDialog}
                            onAthleteCreate={onAthleteCreate}
                            user={p.user}
                            eventRaces={state.data.races}
                            categories={categories}
                            eventYear={p.eventYear}
                            organizationId={p.organizationId}
                            rules={p.rules}
                            preferredRaceId={p.preferredRaceId}
                        />
                    ),
                    (athletes) =>
                        pipe(athletes, () => (
                            <>
                                <Box
                                    sx={{
                                        position: "sticky",
                                        boxShadow:
                                            "0 4px 4px -4px rgba(0, 0, 0, 0.3)",
                                        top: 0,
                                        padding: 1,
                                        zIndex: 2,
                                        backgroundColor: (theme) =>
                                            theme.palette.background.paper,
                                    }}
                                >
                                    <Cart
                                        paymentMethod={p.paymentMethod}
                                        eventId={p.eventId}
                                        items={pipe(
                                            athletes,
                                            array.chain((athlete) =>
                                                pipe(
                                                    athlete.registrations,
                                                    array.filter(
                                                        (reg) =>
                                                            reg.state ===
                                                            "unconfirmed"
                                                    ),
                                                    array.filterMap((reg) =>
                                                        pipe(
                                                            state.data.races,
                                                            registrationFee(
                                                                reg.race_id,
                                                                reg.category_id
                                                            ),
                                                            option.map(
                                                                (
                                                                    fee
                                                                ): CartItem => ({
                                                                    name: athlete.name,
                                                                    born: athlete.born,
                                                                    price: fee,
                                                                    id: reg.id,
                                                                    race_id:
                                                                        reg.race_id,
                                                                    category_id:
                                                                        reg.category_id,
                                                                    race: pipe(
                                                                        state
                                                                            .data
                                                                            .races,
                                                                        array.findFirst(
                                                                            (
                                                                                r
                                                                            ) =>
                                                                                r.id ===
                                                                                reg.race_id
                                                                        ),
                                                                        option.map(
                                                                            (
                                                                                r: EventRace
                                                                            ) =>
                                                                                r.title
                                                                        ),
                                                                        option.toNullable
                                                                    ),
                                                                })
                                                            )
                                                        )
                                                    )
                                                )
                                            ),
                                            array.filter((reg) => reg.price > 0)
                                        )}
                                    />
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 1,
                                        }}
                                    >
                                        <Typography variant={"h5"}>
                                            Moje registrace:
                                        </Typography>
                                        <Button
                                            startIcon={<AddIcon />}
                                            onClick={() => {
                                                addAthleteDialogState.setOpen(
                                                    true
                                                );
                                            }}
                                        >
                                            Přidat závodníka
                                        </Button>
                                    </Box>
                                </Box>
                                <TableContainer>
                                    <Table size={"small"}>
                                        <TableBody>
                                            {athletes.map((athlete) => (
                                                <TableRow
                                                    hover
                                                    key={athlete.id}
                                                >
                                                    <TableCell>
                                                        <strong>
                                                            {athlete.name}
                                                        </strong>
                                                        <br />
                                                        {athlete.born}
                                                        <br />
                                                        <em>{athlete.club}</em>
                                                    </TableCell>
                                                    <TableCell>
                                                        <AthleteRegistrations
                                                            athlete={athlete}
                                                            onRegister={
                                                                onRegister
                                                            }
                                                            onUnregister={
                                                                onUnregister
                                                            }
                                                            eventRaces={
                                                                state.data.races
                                                            }
                                                            categories={
                                                                categories
                                                            }
                                                            eventYear={
                                                                p.eventYear
                                                            }
                                                            organizationId={
                                                                p.organizationId
                                                            }
                                                            rules={p.rules}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Dialog
                                    open={addAthleteDialogState.isOpen}
                                    fullWidth={true}
                                    maxWidth={"md"}
                                    fullScreen={isMobile}
                                >
                                    <CancelableDialogBody
                                        contentSx={{ minHeight: "60vh" }}
                                        onCancel={addAthleteDialogState.close}
                                    >
                                        <CreateAthlete
                                            eventId={p.eventId}
                                            existingAthletes={
                                                state.data.athletes
                                            }
                                            onRegister={onRegisterFromDialog}
                                            organizationId={p.organizationId}
                                            onAthleteCreate={onAthleteCreate}
                                            eventRaces={state.data.races}
                                            categories={categories}
                                            eventYear={p.eventYear}
                                            rules={p.rules}
                                            preferredRaceId={p.preferredRaceId}
                                        />
                                    </CancelableDialogBody>
                                </Dialog>
                            </>
                        ))
                )
            );
    }

    assertUnreachable(state);
};

export default MyRegistrations;

const raceCategoryCols = pipe(
    raceCategoryTable.columns,
    pick("category_id", "entry_fee", "entry_fee_currency")
);
const categoryAlias = "category";
const raceCategoryAlias = raceCategoryTable.name;
const raceFields = pipe(
    raceTable.columns,
    pick(
        "id",
        "title",
        "club_scoring",
        "start_time",
        "registration_fields",
        "event_id"
    )
);
const categoryCols = pipe(
    categoriesTable.columns,
    pick("id", "title", "description", "sex", "age_gte", "age_lte")
);
const categoryCodec = t.type(categoryCols);
type Category = t.TypeOf<typeof categoryCodec>;
const eventRaceCodec = t.type({
    ...raceFields,
    [raceCategoryAlias]: t.array(
        t.type({
            ...raceCategoryCols,
            [categoryAlias]: categoryCodec,
        })
    ),
});
type EventRace = t.TypeOf<typeof eventRaceCodec>;
const eventRacesCodec = t.array(eventRaceCodec);
type EventRaces = t.TypeOf<typeof eventRacesCodec>;

const useEventRaces = (eventId: EventId) => {
    return useQuery(
        "eventRaces",
        (supabase) =>
            supabase
                .from("races")
                .select(
                    columns(
                        raceFields,
                        join(
                            raceCategoryTable,
                            raceCategoryCols,
                            raceCategoryAlias,
                            false,
                            join(categoriesTable, categoryCols, categoryAlias)
                        )
                    )
                )
                .eq("event_id", eventId), // TODO pg-ts for columns, table name
        eventRacesCodec
    );
};

// TODO tmp solution before whole component refactor... should use something like event detail
const categoriesFromRacesTmp = (races: EventRaces): Array<Category> =>
    pipe(
        races,
        array.chain((race) => race[raceCategoryAlias]),
        array.map((raceCat): Category => raceCat.category)
    );

const athleteFields = pipe(
    athleteTable.columns,
    pick("id", "name", "born", "sex", "club")
);

const athleteInsertResultCodec = t.type(athleteFields, "AthleteInsertResult");

const registrationCols = pipe(
    registrationTable.columns,
    pick("id", "race_id", "category_id", "athlete_id", "club", "state")
);

const paymentCols = pipe(paymentTable.columns, pick("id"));
const paymentCodec = t.type(paymentCols);
const paymentAlias = paymentTable.name;

const registrationCodec = t.type(
    {
        ...registrationCols,
        [paymentAlias]: t.array(paymentCodec),
    },
    "Registration"
);
type Registration = t.TypeOf<typeof registrationCodec>;

const registrationInsertResultCodec = t.type(
    registrationCols,
    "RegistrationResult"
);
type RegistrationResult = t.TypeOf<typeof registrationInsertResultCodec>;
const registrationAlias = "registrations";

const myAthleteCodec = t.type({
    ...athleteFields,
    [registrationAlias]: t.array(registrationCodec),
});
type MyAthlete = t.TypeOf<typeof myAthleteCodec>;

// Listed the way class registers are: by surname, then forename, with Czech
// collation so diacritics sort where people expect them. Kept out of
// src/domain, which is bundled into plv8 where Intl may be unavailable.
const nameCollator = new Intl.Collator("cs");
const athleteOrdBySurname: Ord<MyAthlete> = ord.fromCompare((a, b) =>
    pipe(
        nameCollator.compare(
            switchForenameAndSurname(a.name),
            switchForenameAndSurname(b.name)
        ),
        (c) => (c < 0 ? -1 : c > 0 ? 1 : 0)
    )
);

const useMyAthletesWithRaceRegistration = (
    user: User,
    registeredToRaceIdList: Array<RaceId>
) =>
    useQuery(
        "myAthletesWithRaceRegistration",
        (supabase) =>
            supabase
                .from(athleteTable.name)
                .select(
                    columns(
                        athleteFields,
                        join(
                            registrationTable,
                            registrationCols,
                            registrationAlias,
                            false,
                            join(paymentTable, paymentCols, paymentAlias)
                        )
                    )
                )
                .eq("account_id", user.id)
                .in(
                    `${registrationAlias}.${column(
                        registrationTable,
                        "race_id"
                    )}`,
                    registeredToRaceIdList
                ),
        t.array(myAthleteCodec)
    );

const categoriesCodec = t.array(categoryCodec);
type Categories = t.TypeOf<typeof categoriesCodec>;

interface RacesForAthleteParams {
    athlete: Pick<MyAthlete, "born" | "sex">;
    eventRaces: EventRaces;
    categories: Categories;
    eventYear: Year;
}
const raceCategoriesForAthlete = (
    p: Omit<RacesForAthleteParams, "eventRaces">
) => {
    return (race: EventRace) => {
        const categoryByIdMap = pipe(
            p.categories,
            mapFromArray((category) => category.id, identity)
        );

        return pipe(
            race.race_category,
            array.filterMap(({ category_id }) =>
                map.lookup(number.Eq)(category_id, categoryByIdMap)
            ),
            array.filter(isCategoryForAthlete(p.athlete, p.eventYear)),
            nonEmptyArray.fromArray
        );
    };
};
type RaceWithCategoriesForAthlete = {
    race: EventRace;
    categoriesForAthlete: Array<Category>;
};
const racesForAthlete = (
    p: RacesForAthleteParams
): Option<NonEmptyArray<RaceWithCategoriesForAthlete>> => {
    return pipe(
        p.eventRaces,
        array.filterMap((race) =>
            pipe(
                race,
                raceCategoriesForAthlete(p),
                option.map(
                    (categoriesForAthlete): RaceWithCategoriesForAthlete => ({
                        race,
                        categoriesForAthlete,
                    })
                )
            )
        ),
        array.concat(
            // concat races without categories
            pipe(
                p.eventRaces,
                array.filter((race: EventRace) =>
                    array.isEmpty(race.race_category)
                ),
                array.map(
                    (race): RaceWithCategoriesForAthlete => ({
                        race,
                        categoriesForAthlete: [],
                    })
                )
            )
        ),
        nonEmptyArray.fromArray
    );
};

interface AthleteRegistrationsProps extends Handlers, RacesForAthleteParams {
    athlete: MyAthlete;
    organizationId: OrganizationId;
    rules: Nullable<string>;
}

// TODO how to solve no cat/race match athlete?
const AthleteRegistrations = (p: AthleteRegistrationsProps) => {
    return pipe(
        racesForAthlete(p),
        option.map(
            nonEmptyArray.map(
                ({ race, categoriesForAthlete }): RegistrationProps => ({
                    race,
                    organizationId: p.organizationId,
                    rules: p.rules,
                    onRegister: p.onRegister,
                    onUnregister: p.onUnregister,
                    categories: categoriesForAthlete,
                    registration: pipe(
                        p.athlete[registrationAlias],
                        array.findFirst(({ race_id }) => race_id === race.id)
                    ),
                    athlete: p.athlete,
                })
            )
        ),
        optionOrChain(() =>
            pipe(
                p.eventRaces,
                array.map(
                    (race): RegistrationProps => ({
                        race,
                        organizationId: p.organizationId,
                        rules: p.rules,
                        onRegister: p.onRegister,
                        onUnregister: p.onUnregister,
                        categories: p.categories,
                        registration: pipe(
                            p.athlete[registrationAlias],
                            array.findFirst(
                                ({ race_id }) => race_id === race.id
                            )
                        ),
                        athlete: p.athlete,
                    })
                ),
                nonEmptyArray.fromArray
            )
        ),
        option.fold(
            () => <Box>Nenalezen žádný závod k registraci</Box>,
            flow(
                nonEmptyArray.map((props) => (
                    <Registration key={props.race.id} {...props} />
                )),
                (registrations) => <Box>{registrations}</Box>
            )
        )
    );
};

interface RegistrationProps extends Handlers {
    categories: Array<Category>;
    race: EventRace;
    registration: Option<Registration>;
    athlete: MyAthlete;
    organizationId: OrganizationId;
    rules: Nullable<string>;
}

const Registration = (p: RegistrationProps) => {
    return (
        <Box sx={{ display: "flex", alignItems: "center", py: 1 }}>
            {" "}
            <Typography
                variant={"body2"}
                component={"span"}
                sx={{ marginRight: 1 }}
            >
                {p.race.title}
            </Typography>
            {pipe(
                p.registration,
                option.fold(
                    () => <NotRegistered {...p} />,
                    (registration) => (
                        <Registered
                            onRegister={p.onRegister}
                            onUnregister={p.onUnregister}
                            registration={registration}
                            category={pipe(
                                p.categories,
                                array.findFirst(
                                    (cat) => cat.id === registration.category_id
                                )
                            )}
                        />
                    )
                )
            )}
        </Box>
    );
};

const NotRegistered = (p: RegistrationProps) => {
    const dialogState = useDialogState();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    return (
        <DialogButton
            state={dialogState}
            btn={{ children: "registrovat" }}
            dialog={{
                children: (
                    <RegistrationDialog dialogState={dialogState} {...p} />
                ),
                fullScreen: isMobile,
            }}
        />
    );
};

interface RegistrationDialogProps extends RegistrationProps {
    dialogState: DialogState;
}

const RegistrationDialog = (p: RegistrationDialogProps) => {
    return (
        <DialogContent>
            <Finish
                athlete={p.athlete}
                categories={nonEmptyArray.fromArray(p.categories)}
                onReset={p.dialogState.close}
                resetLabel={"storno"}
                organizationId={p.organizationId}
                rules={p.rules}
                race={p.race}
                initFormValues={{
                    categoryId: preselectedCategoryId(p.categories),
                    club: p.athlete.club,
                }}
                onSubmit={(values) =>
                    p.onRegister(
                        registrationPayloadOf(p.race, p.athlete.id, values)
                    )
                }
            />
        </DialogContent>
    );
};

interface RegisteredProps extends Handlers {
    category: Option<Category>;
    registration: Registration;
}
const Registered = (p: RegisteredProps) => {
    const isRegistrationCancelable = p.registration[paymentAlias].length === 0;

    return (
        <>
            <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant={"body2"} component={"span"} sx={{ mx: 1 }}>
                    {pipe(
                        p.category,
                        option.fold(
                            () => <>přihlášen</>,
                            (category) => <CategoryLabel {...category} />
                        )
                    )}
                </Typography>
            </Box>

            <Box>
                <RegistrationStateView state={p.registration.state} />
                {/*// TODO editace registrace do data*/}
                {isRegistrationCancelable && (
                    <Button
                        color={"error"}
                        onClick={() => {
                            p.onUnregister(p.registration);
                        }}
                    >
                        Stornovat
                    </Button>
                )}
            </Box>
        </>
    );
};

// Racer-facing wording of the club field. Event 186 registers school teams, so
// its form says "school"; a per-event setting would be the proper home for this.
type ClubFieldMessages = Messages<{ label: string; required: string }>;
const clubFieldMsg: ClubFieldMessages = messages(
    {
        label: "Club",
        required: "The race has team scoring, fill in the club",
    },
    {
        cs: {
            label: "Tým",
            required: "Závod má týmové hodnocení, vyplňte tým",
        },
    }
);
const schoolFieldMsg: ClubFieldMessages = messages(
    {
        label: "School",
        required: "The race has team scoring, fill in the school",
    },
    {
        cs: {
            label: "Škola",
            required: "Závod má týmové hodnocení, vyplňte školu",
        },
    }
);
const clubFieldOverrides: Partial<Record<number, ClubFieldMessages>> = {
    186: schoolFieldMsg,
};
const clubFieldMessages = (eventId: EventId): ClubFieldMessages =>
    clubFieldOverrides[eventId] ?? clubFieldMsg;

const clubInputCodec = trimmedStringCodec.pipe(
    nonEmptyStringOrNullCodec(athleteTable.columns.club)
);
const createAthleteFormDef = {
    required: {
        name: { codec: fullNameCodec },
        born: {
            codec: trimmedStringCodec.pipe(NumberFromString.pipe(bornCodec)),
        },
        sex: { codec: sexCodec },
    },
    optional: {
        club: {
            codec: clubInputCodec,
        },
    },
};
type CreateAthleteFormValues = FormValues<typeof createAthleteFormDef>;

// athlete going through the add-athlete steps: a new profile from the form,
// or one of the account's athletes picked in the name search
type AthleteToRegister =
    | { kind: "new"; values: CreateAthleteFormValues }
    | { kind: "existing"; athlete: MyAthlete };
const athleteToRegisterValues = (
    athlete: AthleteToRegister
): CreateAthleteFormValues =>
    athlete.kind === "new" ? athlete.values : athlete.athlete;

interface CreateAthleteState {
    type: "createAthlete";
    athlete: Partial<CreateAthleteFormValues>;
}
interface RaceSelectState {
    type: "raceSelect";
    athlete: AthleteToRegister;
}
interface FinishState {
    type: "finish";
    athlete: AthleteToRegister;
    raceId: RaceId;
    initFormValues: Partial<FinishFormValues>;
}
type State = CreateAthleteState | RaceSelectState | FinishState;

const stepsLabels: Record<State["type"], string> = {
    createAthlete: "Profil nového závodníka",
    raceSelect: "Výběr závodu",
    finish: "Dokončení registrace",
};
interface CreateAthleteProps {
    eventId: EventId;
    // account's athletes offered by the name search; picking one registers
    // it through onRegister instead of creating a profile
    existingAthletes: Array<MyAthlete>;
    onRegister: Handlers["onRegister"];
    onAthleteCreate: (
        athlete: CreateAthleteFormValues,
        finish: FinishFormValues,
        race: EventRace
    ) => Task<boolean>;
    eventRaces: EventRaces;
    categories: Categories;
    eventYear: Year;
    organizationId: OrganizationId;
    rules: Nullable<string>;
    user?: User;
    preferredRaceId: Option<RaceId>;
}
const CreateAthlete = (p: CreateAthleteProps) => {
    const [state, setState] = React.useState<State>({
        type: "createAthlete",
        athlete: pipe(
            p.user,
            option.fromNullable,
            option.chain(
                flow(
                    (u) => u.user_metadata,
                    wellKnownUserMetadataCodec.decode,
                    option.fromEither,
                    option.chain((meta) =>
                        pipe(
                            option.fromNullable(meta.full_name),
                            optionOrChain(() => option.fromNullable(meta.name))
                        )
                    )
                )
            ),
            option.fold(
                () => ({}),
                (nameFromMetadata) => ({
                    name: nameFromMetadata as FullName,
                })
            )
        ),
    });

    // races still open to the athlete: all of them for a new profile, the
    // not-yet-registered ones for an existing athlete
    const openRaces = (athlete: AthleteToRegister): EventRaces =>
        athlete.kind === "new"
            ? p.eventRaces
            : p.eventRaces.filter(
                  (race) =>
                      !athlete.athlete[registrationAlias].some(
                          (registration) => registration.race_id === race.id
                      )
              );

    // athletes already registered everywhere have nothing left to pick
    const searchableAthletes = p.existingAthletes.filter(
        (athlete) => openRaces({ kind: "existing", athlete }).length > 0
    );

    // straight to Finish when exactly one race fits, else race selection
    const proceedWith = (athlete: AthleteToRegister) =>
        pipe(
            racesForAthlete({
                ...p,
                eventRaces: openRaces(athlete),
                athlete: athleteToRegisterValues(athlete),
            }),
            option.filter((races) => races.length === 1),
            option.fold(
                (): State => ({ type: "raceSelect", athlete }),
                flow(
                    nonEmptyArray.head,
                    (race): State => ({
                        type: "finish",
                        raceId: race.race.id,
                        athlete,
                        initFormValues: {
                            categoryId: preselectedCategoryId(
                                race.categoriesForAthlete
                            ),
                        },
                    })
                )
            ),
            setState
        );

    return pipe(
        state,
        (state) => {
            switch (state.type) {
                case "createAthlete":
                    return (
                        <CreateAthleteForm
                            eventId={p.eventId}
                            organizationId={p.organizationId}
                            defaultValues={state.athlete}
                            existingAthletes={searchableAthletes}
                            onPickExisting={(athlete) =>
                                proceedWith({ kind: "existing", athlete })
                            }
                            onSubmit={(values) =>
                                proceedWith({ kind: "new", values })
                            }
                        />
                    );

                case "raceSelect":
                    return (
                        <RaceSelect
                            athlete={athleteToRegisterValues(state.athlete)}
                            eventRaces={openRaces(state.athlete)}
                            eventYear={p.eventYear}
                            categories={p.categories}
                            preferredRaceId={p.preferredRaceId}
                            onSubmit={(values) => {
                                const preselectedCategoryId = pipe(
                                    p.eventRaces,
                                    array.findFirst(
                                        (race) => race.id === values.raceId
                                    ),
                                    option.chain(
                                        raceCategoriesForAthlete({
                                            athlete: athleteToRegisterValues(
                                                state.athlete
                                            ),
                                            categories: p.categories,
                                            eventYear: p.eventYear,
                                        })
                                    ),
                                    option.filter(
                                        // preselect only if single category match
                                        (categories) => categories.length === 1
                                    ),
                                    option.map(
                                        flow(
                                            nonEmptyArray.head,
                                            (category) => category.id
                                        )
                                    ),
                                    option.toUndefined
                                );

                                setState({
                                    type: "finish",
                                    raceId: values.raceId,
                                    athlete: state.athlete,
                                    initFormValues: {
                                        categoryId: preselectedCategoryId,
                                        club: athleteToRegisterValues(
                                            state.athlete
                                        ).club,
                                    },
                                });
                            }}
                        />
                    );

                case "finish":
                    const race = pipe(
                        p.eventRaces,
                        array.findFirst((race) => race.id === state.raceId),
                        someOrThrow
                    );

                    const athlete = state.athlete;
                    const categories = pipe(
                        race,
                        raceCategoriesForAthlete({
                            eventYear: p.eventYear,
                            categories: p.categories,
                            athlete: athleteToRegisterValues(athlete),
                        })
                    );

                    return (
                        <Finish
                            race={race}
                            athlete={athleteToRegisterValues(athlete)}
                            categories={categories}
                            initFormValues={state.initFormValues}
                            organizationId={p.organizationId}
                            rules={p.rules}
                            onSubmit={(finishFormValues) =>
                                athlete.kind === "new"
                                    ? p.onAthleteCreate(
                                          athlete.values,
                                          finishFormValues,
                                          race
                                      )
                                    : p.onRegister(
                                          registrationPayloadOf(
                                              race,
                                              athlete.athlete.id,
                                              finishFormValues
                                          )
                                      )
                            }
                            onReset={() => {
                                setState({
                                    type: "createAthlete",
                                    athlete: {},
                                });
                            }}
                        />
                    );
            }
            assertUnreachable(state);
        },
        (stepContent) => (
            <>
                <Stepper sx={{ mt: 3, mb: 2 }}>
                    <Step
                        active={state.type === "createAthlete"}
                        completed={
                            state.type === "raceSelect" ||
                            state.type === "finish"
                        }
                    >
                        <StepLabel>{stepsLabels.createAthlete}</StepLabel>
                    </Step>
                    <Step
                        active={state.type === "raceSelect"}
                        completed={
                            state.type === "raceSelect" ||
                            state.type === "finish"
                        }
                    >
                        <StepLabel>{stepsLabels.raceSelect}</StepLabel>
                    </Step>
                    <Step active={state.type === "finish"}>
                        <StepLabel>{stepsLabels.finish}</StepLabel>
                    </Step>
                </Stepper>
                {stepContent}
            </>
        )
    );
};

interface CreateAthleteFormProps {
    eventId: EventId;
    organizationId: OrganizationId;
    existingAthletes: Array<MyAthlete>;
    onPickExisting: (athlete: MyAthlete) => void;
    onSubmit: (values: CreateAthleteFormValues) => void;
    defaultValues?: Partial<CreateAthleteFormValues>;
}
const CreateAthleteForm = (p: CreateAthleteFormProps) => {
    const lastInsertedClubState = useLocalStorage(
        lastInsertedClubStorageKey,
        pipe(
            p.defaultValues,
            option.fromNullable,
            option.chain((defaults) => option.fromNullable(defaults.club)),
            option.toNullable
        )
    );

    const form = useForm(
        createAthleteFormDef,
        (values) => {
            lastInsertedClubState.set(values.club);
            p.onSubmit(values);
        },
        {
            ...p.defaultValues,
            club: pipe(
                lastInsertedClubState.get(),
                createAthleteFormDef.optional.club.codec.decode,
                option.fromEither,
                option.toUndefined
            ),
        }
    );

    const t = useTranslation(athleteMessages);
    const clubM = useTranslation(clubFieldMessages(p.eventId));

    return (
        <form {...form.formProps}>
            <Grid
                container
                direction={"column"}
                spacing={2}
                sx={{ p: 1, my: 1 }}
            >
                <Grid item>
                    <AthleteNameAutocomplete
                        {...omit(form.req.name, "ref")}
                        label={t.name}
                        fullWidth
                        athletes={p.existingAthletes}
                        onPick={p.onPickExisting}
                    />
                </Grid>
                <Grid item>
                    <TextField
                        {...form.req.born}
                        label={t.born}
                        fullWidth
                        type={"number"}
                    />
                </Grid>
                <Grid item>
                    <SexSelect {...form.req.sex} />
                </Grid>
                <Grid item>
                    <ClubAutocomplete
                        {...form.opt.club}
                        organizationId={p.organizationId}
                        label={clubM.label}
                        fullWidth
                    />
                </Grid>
                <Grid item sx={{ textAlign: "right" }}>
                    <Button type={"submit"} variant={"contained"}>
                        Pokračovat
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

const raceSelectFormDef = {
    required: {
        raceId: {
            codec: raceIdCodec,
        },
    },
    optional: {},
};
type RaceSelectFormValues = FormValues<typeof raceSelectFormDef>;
interface RaceSelectOption extends Pick<EventRace, "id" | "title"> {
    categoriesForAthlete?: Categories;
}
interface RaceSelect extends RacesForAthleteParams {
    athlete: CreateAthleteFormValues;
    onSubmit: (values: RaceSelectFormValues) => void;
    preferredRaceId: Option<RaceId>;
}
const RaceSelect = (p: RaceSelect) => {
    const raceOptions: Array<RaceSelectOption> = pipe(
        racesForAthlete(p),
        option.fold(
            () => p.eventRaces.map(pick("id", "title")),
            nonEmptyArray.map(({ race, categoriesForAthlete }) => ({
                ...pipe(race, pick("id", "title")),
                categoriesForAthlete,
            }))
        )
    );
    const form = useForm(raceSelectFormDef, p.onSubmit, {
        raceId: pipe(
            // prefer the linked race when it is among the selectable options,
            // otherwise default to the first option
            p.preferredRaceId,
            option.filter((preferredId) =>
                raceOptions.some((r) => r.id === preferredId)
            ),
            optionOrChain(() =>
                pipe(
                    array.head(raceOptions),
                    option.map((r) => r.id)
                )
            ),
            option.toNullable
        ),
    });

    return (
        <form {...form.formProps}>
            <Grid
                container
                direction={"column"}
                spacing={2}
                sx={{ p: 1, my: 1 }}
            >
                <Grid item>
                    <Select {...form.req.raceId} label={"Závod"} fullWidth>
                        {raceOptions.map((raceOption) => (
                            <MenuItem key={raceOption.id} value={raceOption.id}>
                                {raceOption.title}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
                <Grid item textAlign={"right"}>
                    <Button type={"submit"} variant={"contained"}>
                        Pokračovat
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

const finishFormDef = {
    required: {
        // raceConditionsAgreement: { codec: t.literal(true) }, TODO add to event setting
        xathloConditionsAgreement: { codec: t.literal(true) },
    },
    optional: {
        // TODO this should probably be more dynamic, custom fields etc... this specific validation is right now done in onsubmit
        categoryId: { codec: categoriesTable.columns.id },
        club: {
            codec: clubInputCodec,
        },
    },
};
type FinishFormValues = FormValues<typeof finishFormDef>;
interface FinishProps {
    onSubmit: (values: FinishFormValues) => Task<unknown>;
    initFormValues: Partial<FinishFormValues>;
    race: EventRace;
    athlete: CreateAthleteFormValues;
    organizationId: OrganizationId;
    rules: Nullable<string>;
    categories: Option<NonEmptyArray<Category>>;
    onReset: () => void;
    resetLabel?: string;
}
const Finish = (p: FinishProps) => {
    const lastInsertedClubState = useLocalStorage(
        lastInsertedClubStorageKey,
        pipe(p.initFormValues.club, option.fromNullable, option.toNullable)
    );
    const [isLoading, setIsLoading] = React.useState(false);
    const sexT = useTranslation(sexMessages);
    const categoryT = useTranslation(categoryMessages);
    const raceT = useTranslation(raceMessages);
    const clubM = useTranslation(clubFieldMessages(p.race.event_id));

    const { race, categories } = p;
    const isRaceWithClubScoring = race.club_scoring != null;

    const formDef = React.useMemo(() => {
        const customFields = pipe(
            p.race.registration_fields,
            option.fromNullable,
            option.getOrElse(emptyArray<RegistrationField>())
        );

        const required: Record<string, any> = { ...finishFormDef.required };
        const optional: Record<string, any> = { ...finishFormDef.optional };

        customFields.forEach((field) => {
            const key = customFieldKey(field);
            const inputDef = customFieldInputDef(field);
            if (field.required) {
                required[key] = inputDef;
            } else {
                optional[key] = inputDef;
            }
        });

        return { required, optional };
    }, [finishFormDef, p.race.registration_fields]);

    const form = useForm(
        formDef,
        (values) => {
            // TODO registration validation should probably move to server... post request and you are in different category...
            let valid = true;

            if (option.isSome(categories) && values.categoryId == null) {
                valid = false;
                form.formHooks.setError("categoryId", {
                    type: "required",
                    message: "Vyberte kategorii",
                });
            }

            if (isRaceWithClubScoring && values.club == null) {
                valid = false;
                form.formHooks.setError("club", {
                    type: "required",
                    message: clubM.required,
                });
            }

            if (valid) {
                lastInsertedClubState.set(values.club);
                setIsLoading(true);
                pipe(
                    values as FinishFormValues,
                    p.onSubmit,
                    task.map(() => {
                        setIsLoading(false);
                    }),
                    runTask
                );
            }
        },

        {
            ...p.initFormValues,
            club: pipe(
                p.athlete.club,
                option.fromNullable,
                optionOrChain(() =>
                    pipe(
                        lastInsertedClubState.get(),
                        clubInputCodec.decode,
                        option.fromEither
                    )
                ),
                option.toUndefined
            ),
        }
    );

    const currentCategoryId = form.formHooks.watch("categoryId");
    const currentFee = pipe(
        currentCategoryId,
        option.fromNullable,
        option.chain((catId) =>
            pipe(
                race.race_category,
                array.findFirst((c) => c.category_id === catId),
                option.chain((c) => option.fromNullable(c.entry_fee))
            )
        ),
        option.getOrElse(() => 0)
    );

    return (
        <form {...form.formProps}>
            <Grid
                container
                direction={"column"}
                spacing={2}
                sx={{ p: 1, mb: 1 }}
            >
                <Grid item>
                    <Typography variant={"caption"}>Závodník</Typography>
                    <Typography variant={"subtitle1"}>
                        <strong>{p.athlete.name}</strong> ({sexT[p.athlete.sex]}
                        , {p.athlete.born})
                    </Typography>
                </Grid>

                <Grid item>
                    <Typography variant={"caption"}>Závod</Typography>
                    <Typography variant={"subtitle1"}>
                        <strong>{race.title}</strong>
                    </Typography>
                </Grid>

                {pipe(
                    categories,
                    option.fold(constNull, (nonEmptyCategories) => (
                        <Grid item>
                            <Select
                                {...form.opt.categoryId}
                                required
                                label={"Start v kategorii"}
                                fullWidth
                            >
                                {pipe(
                                    nonEmptyCategories,
                                    nonEmptyArray.map((category) => (
                                        <MenuItem
                                            key={category.id}
                                            value={category.id}
                                        >
                                            {categoryLabel(
                                                category,
                                                categoryT,
                                                pipe(
                                                    race.start_time,
                                                    validIsoDateStringFromDateCodec.encode,
                                                    (startDate) =>
                                                        startDate.getFullYear()
                                                )
                                            )}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </Grid>
                    ))
                )}
                <Grid item>
                    <ClubAutocomplete
                        {...form.opt.club}
                        required={isRaceWithClubScoring}
                        organizationId={p.organizationId}
                        label={clubM.label}
                        fullWidth
                    />
                </Grid>
                {pipe(
                    p.race.registration_fields,
                    option.fromNullable,
                    option.getOrElse(emptyArray<RegistrationField>()),
                    array.filterMap((field) => {
                        const key = customFieldKey(field);
                        const bucket = field.required ? form.req : form.opt;
                        return pipe(
                            bucket as Record<string, any>,
                            record.lookup(key),
                            option.map((formProps) => ({ formProps, field }))
                        );
                    }),
                    array.map(({ field, formProps }) => (
                        <Grid item key={customFieldKey(field)}>
                            <RegistrationDynamicField
                                eventId={p.race.event_id}
                                field={field}
                                formProps={formProps}
                            />
                        </Grid>
                    ))
                )}
                <Grid item>
                    <RequiredCheckbox
                        {...form.req.xathloConditionsAgreement}
                        label={
                            // TODO link to xathlo, more systematic, store agreement in db
                            // create agreement component (with link to current version)
                            <>
                                Závodník souhlasí se zpracováním uvedených údajů
                                dle zásad aplikace{" "}
                                <InternalLink href={privacyRoute.route({})}>
                                    <Link target="_blank" rel="noreferrer">
                                        xathlo.com
                                    </Link>
                                </InternalLink>
                                {p.rules != null && (
                                    <span>
                                        {" "}
                                        a s <EventRulesLink rules={p.rules} />
                                    </span>
                                )}
                            </>
                        }
                    />
                </Grid>
                {/*<Grid item>*/}
                {/*    <RequiredCheckbox*/}
                {/*        {...form.req.raceConditionsAgreement}*/}
                {/*        label={*/}
                {/*            // TODO link to event detail more systematic, store agreement in db*/}
                {/*            // create agreement component (with link to current version)*/}
                {/*            "Závodník souhlasí s účastí na vlastní nebezpečí, pořadatel neručí za škody účastníkům vzniklé ani jimi způsobené. Všichni účastníci musí dodržovat ustanoveni zákona č.3611/2000 Sb."*/}
                {/*        }*/}
                {/*    />*/}
                {/*</Grid>*/}
                <Grid item textAlign={"right"}>
                    {currentFee <= 0 ? null : (
                        <Typography
                            sx={{
                                ml: 2,
                            }}
                            component={"span"}
                            variant={"h6"}
                            fontWeight={"normal"}
                        >
                            {raceT.fee}:{" "}
                            <strong>
                                <LocalCurrency
                                    value={currentFee}
                                    currency={"CZK"}
                                />
                            </strong>
                        </Typography>
                    )}
                </Grid>
                <Grid item container justifyContent={"space-between"}>
                    <Grid item>
                        <Button variant={"outlined"} onClick={p.onReset}>
                            {p.resetLabel == null ? "Reset" : p.resetLabel}
                        </Button>
                    </Grid>
                    <Grid item>
                        <LoadingButton
                            loading={isLoading}
                            onClick={form.formProps.onSubmit}
                            variant={"contained"}
                        >
                            {currentFee > 0
                                ? "přidat do košíku"
                                : "dokončit registraci"}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </Grid>
        </form>
    );
};

// Event rules are configured per event. A valid URL opens in a new tab,
// any other (plain) text is shown in a dialog.
const EventRulesLink = (p: { rules: string }) => {
    const dialogState = useDialogState();
    const label = "podmínkami akce";

    if (urlCodec.is(p.rules)) {
        return (
            <Link href={p.rules} target="_blank" rel="noreferrer">
                {label}
            </Link>
        );
    }

    return (
        // avoid toggling the surrounding checkbox / submitting the form
        <Box
            component={"span"}
            onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
                e.preventDefault();
                e.stopPropagation();
            }}
        >
            <DialogButton
                state={dialogState}
                btn={{ type: "button", children: label }}
                dialog={{
                    fullWidth: true,
                    maxWidth: "sm",
                    children: (
                        <CancelableDialogBody
                            title={"Podmínky akce"}
                            onCancel={dialogState.close}
                        >
                            <Typography sx={{ whiteSpace: "pre-wrap" }}>
                                {p.rules}
                            </Typography>
                        </CancelableDialogBody>
                    ),
                }}
            />
        </Box>
    );
};

const preselectedCategoryId = (categories: Array<Category>) =>
    categories.length === 1 ? categories[0].id : undefined;
const lastInsertedClubStorageKey = "myRegistrationsLastInsertedClub";
